import { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import Icon from "../components/em/Icon";
import { hasPermission, hasAnyPermission } from "../utils/permissionUtils";
import { logout } from "../api/auth";





const Menu = ({ onNavClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem("role");

  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const createMenuRef = useRef(null);

  const basePath =
    role === "Admin"
      ? "/admin"
      : role === "HR"
        ? "/hr"
        : role === "member"
          ? "/member"
          : "";

  const handleLogout = () => {
    logout();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target)) {
        setShowCreateMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setShowCreateMenu(false);
    document.body.classList.remove("menu-active");
  }, [location.pathname]);

  // Exactly the keys the items below are gated on — nothing more. Keyed off
  // `view_dashboard` before, which meant a user could open the Create menu and
  // find it empty; it also listed `create_employee` / `edit_employee`, which
  // are not permissions that exist in the catalog at all.
  // Keep this list in step with the items rendered inside the dropdown.
  const canShowCreateMenu = hasAnyPermission([
    "view_notes",
    "view_signoff",
    "view_profile_generator",
    "view_signatures",
    "generate_letters",
    "manage_star_of_month",
    "manage_newsletter",
  ]);
  const getRoutePath = (key) => {
    switch (key) {
      case "dashboard":
        return role === "Admin"
          ? "/admin/dashboard"
          : role === "HR"
            ? "/hr/dashboard"
            : "/member/dashboard";

      case "calendar":
        return role === "Admin"
          ? "/admin/calendar"
          : role === "HR"
            ? "/hr/calendar"
            : "/member/calendar";

      case "settings":
        return role === "Admin"
          ? "/admin/settings"
          : role === "HR"
            ? "/hr/settings"
            : "/member/settings";

      case "teams":
        return role === "Admin"
          ? "/admin/teams"
          : role === "HR"
            ? "/hr/teams"
            : "/member/teams";

      case "notes":
        return `${basePath}/notes`;

      case "workspace":
        return role === "Admin"
          ? "/admin/workspace"
          : role === "HR"
            ? "/hr/workspace"
            : "/member/workspace";

      case "attendance":
        return role === "Admin"
          ? "/admin/attendance"
          : role === "HR"
            ? "/hr/attendance"
            : "/member/attendance";

      case "leaves":
        return role === "Admin"
          ? "/admin/leaves"
          : role === "HR"
            ? "/hr/leaves"
            : "/member/leaves";

      case "shifts":
        return role === "Admin"
          ? "/admin/shifts"
          : role === "HR"
            ? "/hr/shifts"
            : "/member/shifts";

      case "orgchart":
        return role === "Admin"
          ? "/admin/OrgChart"
          : role === "HR"
            ? "/hr/OrgChart"
            : "/member/OrgChart";

      // Letters and signatures are role-scoped (Admin + HR routes both exist);
      // the menu entries stay gated by generate_letters / view_signatures.
      case "generateletter":
        return `${basePath}/generateletter`;

      case "signature":
        return `${basePath}/signature`;

      default:
        return "/";
    }
  };
  return (
    <div className="mainMenu">
      <ul>
        {hasPermission("view_dashboard") && (
          <li>
            <NavLink
              to={getRoutePath("dashboard")}
              className="menu-item"
              onClick={onNavClick}
            >
              <span className="dashboard">
                <i className="icon-wrap">
                  <Icon name="home" />
                </i>
                {role === "HR"
                  ? "Hr Dashboard"
                  : role === "member"
                    ? "Dashboard"
                    : "Dashboard"}
              </span>
            </NavLink>
          </li>
        )}

        {canShowCreateMenu && (
          <li className="dropdown" ref={createMenuRef}>
            <span
              className="create"
              onClick={() => setShowCreateMenu((prev) => !prev)}
            >
              <i className="icon-wrap">
                <Icon name="create" />
              </i>
              Create
            </span>

            {showCreateMenu && (
              <ul className="dropdown-menu">
                {/* Notes — granted to every role by default, but still gated:
                    the link must disappear if `view_notes` is revoked, since
                    the route behind it enforces that key. */}
                {hasPermission("view_notes") && (
                  <li className="mng-usr">
                    <NavLink
                      to={getRoutePath("notes")}
                      onClick={() => {
                        onNavClick();
                        setShowCreateMenu(false);
                      }}
                    >
                      <i className="icon-wrap">
                        <Icon name="create" />
                      </i>
                      Notes
                    </NavLink>
                  </li>
                )}

                {/* Sign-off Documents — read & acknowledge for everyone holding
                    `view_signoff`; managing the library is a separate key. */}
                {hasPermission("view_signoff") && (
                  <li className="mng-usr">
                    <NavLink
                      to={`${basePath}/signoff-documents`}
                      onClick={() => {
                        onNavClick();
                        setShowCreateMenu(false);
                      }}
                    >
                      <i className="icon-wrap">
                        <Icon name="create" />
                      </i>
                      Sign-off Documents
                    </NavLink>
                  </li>
                )}

                {/* Profile Generator — hidden from the Create menu; route and
                    page stay. Restore by un-commenting this block.
                {hasPermission("view_profile_generator") && (
                  <li className="mng-usr">
                    <NavLink
                      to={`${basePath}/profile-generator`}
                      onClick={() => {
                        onNavClick();
                        setShowCreateMenu(false);
                      }}
                    >
                      <i className="icon-wrap">
                        <Icon name="create" />
                      </i>
                      Profile Generator
                    </NavLink>
                  </li>
                )} */}

                {hasPermission("view_signatures") && (
                  <li className="mng-sign">
                    <NavLink
                      to={getRoutePath("signature")}
                      onClick={() => {
                        onNavClick();
                        setShowCreateMenu(false);
                      }}
                    >
                      <i className="icon-wrap">
                        <Icon name="create" />
                      </i>
                      Generate Signatures
                    </NavLink>
                  </li>
                )}

                {/* Generate Letter — hidden from the Create menu; route and
                    page stay. Restore by un-commenting this block.
                {hasPermission("generate_letters") && (
                  <li className="mng-usr">
                    <NavLink
                      to={`${basePath}/generateletter`}
                      onClick={() => {
                        onNavClick();
                        setShowCreateMenu(false);
                      }}
                    >
                      <i className="icon-wrap">
                        <Icon name="mail" />
                      </i>
                      Generate Letter
                    </NavLink>
                  </li>
                )} */}

                {hasPermission("manage_star_of_month") && (
                  <li className="mng-usr">
                    <NavLink
                      to={`${basePath}/star-of-the-month`}
                      onClick={() => {
                        onNavClick();
                        setShowCreateMenu(false);
                      }}
                    >
                      <i className="icon-wrap">
                        <Icon name="create" />
                      </i>
                      Star of the Month
                    </NavLink>
                  </li>
                )}

                {hasPermission("manage_newsletter") && (
                  <li className="mng-usr">
                    <NavLink
                      to={`${basePath}/newsletters`}
                      onClick={() => {
                        onNavClick();
                        setShowCreateMenu(false);
                      }}
                    >
                      <i className="icon-wrap">
                        <Icon name="mail" />
                      </i>
                      Newsletter
                    </NavLink>
                  </li>
                )}
              </ul>
            )}
          </li>
        )}

        {hasPermission("view_employees") && (
          <li>
            <NavLink
              to={getRoutePath("teams")}
              className="menu-item"
              onClick={onNavClick}
            >
              <span className="teams">
                <i className="icon-wrap">
                  <Icon name="user" />
                </i>
                Employees
              </span>
            </NavLink>
          </li>
        )}

        {hasPermission("view_workspace") && (
          <li>
            <NavLink
              to={getRoutePath("workspace")}
              className="menu-item"
              onClick={onNavClick}
            >
              <span className="workspace">
                <i className="icon-wrap">
                  <Icon name="folder" />
                </i>
                Workspace
              </span>
            </NavLink>
          </li>
        )}

        {hasPermission("view_attendance") && (
          <li>
            <NavLink
              to={getRoutePath("attendance")}
              className="menu-item"
              onClick={onNavClick}
            >
              <span className="clock">
                <i className="icon-wrap">
                  <Icon name="clock" />
                </i>
                Attendance
              </span>
            </NavLink>
          </li>
        )}

        {hasPermission("view_calendar") && (
          <li>
            <NavLink
              to={getRoutePath("calendar")}
              className="menu-item"
              onClick={onNavClick}
            >
              <span className="calendar">
                <i className="icon-wrap">
                  <Icon name="calendar" />
                </i>
                Calendar
              </span>
            </NavLink>
          </li>
        )}

        {hasPermission("view_leaves") && (
          <li>
            <NavLink
              to={getRoutePath("leaves")}
              className="menu-item"
              onClick={onNavClick}
            >
              <span className="leaves">
                <i className="icon-wrap">
                  <Icon name="leaves" />
                </i>
                Leaves
              </span>
            </NavLink>
          </li>
        )}

        {hasPermission("view_org_chart") && (
          <li>
            <NavLink
              to={getRoutePath("orgchart")}
              className="menu-item"
              onClick={onNavClick}
            >
              <span className="orgn-icn">
                <i className="icon-wrap">
                  <Icon name="chart" />
                </i>
                Org. Chart
              </span>
            </NavLink>
          </li>
        )}
        {hasPermission("view_shifts") && (
          <li>
            <NavLink
              to={getRoutePath("shifts")}
              className="menu-item"
              onClick={onNavClick}
            >
              <span className="shift-icn">
                <i className="icon-wrap">
                  <Icon name="shift" />
                </i>
                Shifts
              </span>
            </NavLink>
          </li>
        )}
      </ul>
    </div>
  );
};

Menu.propTypes = {
  onNavClick: PropTypes.func,
};

export default Menu;
